/**
 * Transform Listing from API to ListingDetails used in the app.
 *
 * This takes a "listing" from the API and returns a "listingDetail" item, which is shaped
 * a bit more towards how the app is using the information. It mostly equals the API listing "details"
 * but e.g. media is transformed to suit the app needs.
 */
export function transformListingDetails(listing: ApiSchema['Listing'] | undefined): ListingDetails {
    const listingDetails = {
        title: listing?.title ?? '',
        lead: listing?.details?.importantInfo ?? '',
        description: listing?.details?.description,
        startingPrice: listing?.details?.startingPrice,
        included: listing?.details?.included,
        highlights: listing?.details?.highlights,
        startingPoint: listing?.details?.startingPoint,
        media: {
            teaser: undefined,
            images: [],
            videos: [],
            map: {
                small: undefined,
                large: undefined,
            },
        } as ListingDetails['media'],
    } as ListingDetails;

    // Listing is determined to be an Activity if it has activityParts
    if (listing?.activityParts?.length) {
        const listingDetailsActivity = listingDetails as ListingDetailsActivity;
        listingDetailsActivity.type = 'Activity';
        listingDetailsActivity.supplier = listing?.activityParts?.[0]?.supplier?.name ?? '';
    }
    // Otherwise, it is a P2P listing, if it includes ":trips:" in its id
    else if (listing?.id?.includes(':trips:')) {
        const listingDetailsP2P = listingDetails as ListingDetailsP2P;
        listingDetailsP2P.type = 'P2P';
        const destinationImage = listing?.details?.media?.images?.find(image => image.tags?.includes('DESTINATION'));
        const destinationVideo = listing?.details?.media?.videos?.find(video => video.tags?.includes('DESTINATION'));

        listingDetailsP2P.media.journeyDestination = {
            label: listing?.title,
            image: destinationImage?.url,
            video: destinationVideo?.url,
        };
    }

    const teaserImage = listing?.details?.media?.images?.find(image => image.tags?.includes('TEASER'));
    if (teaserImage?.url) {
        listingDetails.media.teaser = {
            url: teaserImage.url,
        };
    }

    // All non-tagged images are considered to be "normal" images (not teaser, maps or destination images)
    listingDetails.media.images = listing?.details?.media?.images
        ?.filter(image => !image.tags?.length)
        ?.map(image => ({
            url: image.url,
        }));

    // Map images are determined by the "MAP" tag. We flatten both the images and its "variants", which may container
    // alternative images to be used for small/medium map images
    const mapImages = [
        ...(listing?.details?.media?.images?.filter(image => image.tags?.includes('MAP')) ?? []),
        ...listing?.details?.media?.images?.filter(image => image.tags?.includes('MAP'))?.flatMap(image => image.variants) ?? [],
    ];

    // Consider the smallest map image to be the "small" map image, and the largest to be the "large" map image
    // Quite a volatile solution, but that's it for now.
    const mapImageSmall = mapImages.sort((a, b) => (a?.width ?? Number.MAX_VALUE) - (b?.width ?? Number.MAX_VALUE))?.[0];
    if (mapImageSmall) {
        listingDetails.media.map.small = {
            url: mapImageSmall?.url,
        };
    }

    // Consider the largest map image to be the "large" map image.
    const mapImageLarge = mapImages.sort((a, b) => (b?.width ?? 0) - (a?.width ?? 0))?.[0];
    if (mapImageLarge) {
        listingDetails.media.map.large = {
            url: mapImageLarge?.url,
        };
    }

    return listingDetails;
}
